import { useMemo } from 'react';

import { variant } from 'styled-system';

import { useDeprecate } from '@adsk/alloy-react-helpers';
import { useTheme } from '@adsk/alloy-react-styles';

import { IBoxProps } from '../Box';

const DEPRECATED_COLORS = new Set([
  '_adskBlue100Alpha',
  '_blueShadow',
  '_greyAlpha',
]);

/**
 * Resolves Box theming from the current Weave 2.0 theme context.
 *
 * Returns memoised `typography` and `ellipsis` style-system variants
 * built from the active theme tokens.  Emits deprecation warnings when
 * legacy colour aliases are used.
 *
 * Color resolution is handled by styled-system through the ThemeProvider
 * in Theme.tsx, which already maps to Weave 2.0 tokens.
 */
export const useBoxTheme = ({ color, bg }: IBoxProps) => {
  const theme = useTheme();

  const typography = useMemo(
    () =>
      variant({
        prop: 'typography',
        variants: theme.tokens?.component?.typography,
      }),
    [theme],
  );

  const ellipsis = useMemo(
    () =>
      variant({
        prop: 'ellipsis',
        variants: {
          single: theme.tokens?.component?.typography?.ellipsis,
          multi: theme.tokens?.component?.typography?.lineClamp,
        },
      }),
    [theme],
  );

  useDeprecate(
    !!color && DEPRECATED_COLORS.has(color),
    'Box',
    `color="${color}"`,
    'Weave 2.0 theme colors',
  );

  useDeprecate(
    !!bg && DEPRECATED_COLORS.has(bg),
    'Box',
    `bg="${bg}"`,
    'Weave 2.0 theme colors',
  );

  return {
    ellipsis,
    typography,
  };
};
